import { Grid, makeStyles, FormGroup, FormControlLabel, Typography, Checkbox } from '@material-ui/core';
import { AntTab, AntTabs } from '../AntTab';
import RouteLeavingGuard from '../PageLeaveConfirmationDialog/RouteLeavingGuard';
import { AnnoNode, Annotation, EditorialStatus, EditorialType, Node, NodeTypeName } from '../../domain/domain';
import qs from 'qs';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplicationState } from '../../reducers';
import { buildComponentPageURL, findAllowedEditorialTypesForNodeType, getEditorialContentTypeName } from '../../utils';
import EditorialContentToolbar from './EditorialContentToolbar';
import { getEditorialContentByType, isShowRevisions, findSubTreeByNodeId, getEditorialPlaceholder } from './utils';
import DMP from './DMP';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import ApproveEditorialContentDialog from './ApproveEditorialContentDialog';
import RejectEditorialContentDialog from './RejectEditorialContentDialog';
import { saveEditorialContent } from '../../api/node';
import TextEditorDialog from './TextEditorDialog';
import TextEditor from '../TextEditor/TextEditor';
import isEqual from 'lodash/isEqual';

const useStyles = makeStyles((_) => ({
    root: {
        border: "1px solid #ddd",
        height: "100%",
    },
    editorArea: {
        border: "1px solid #ddd",
        padding: 10,
        height: "100%",
        overflow: "hidden",
        marginRight: 32,
    },
    buttonArea: {
        padding: 10,
        marginRight: 32,
    },
    tabs: {
        paddingLeft: 10,
    },
    emptyTab: {
        color: "#595959",
    },
    tabWithContent: {
        fontWeight: 700,
    },
    tabWithNotApprovedChanges: {
        color: "#dc143c",
    },
    diffCSS: {
        borderTop: "1px solid #ddd",
        fontSize: "12px",
        paddingTop: 10,
        minHeight: 300,
    },
    maxHeight: {
        height: "100%",
    },
    topPanel: {
        height: "75%",
    },
    approvedDate: {
        fontWeight: "normal",
        fontSize: 13
    },
}));

const EditorialLevelAnnotation = Object.freeze({
    workingCopy: EditorialType.ANNOTATION,
    approvedCopy: EditorialType.ANNOTATION
});

type Props = {
    onApproveEditorialContent: (node: Node, editorialType: EditorialType) => void,
    onRejectEditorialContent: (node: Node, editorialType: EditorialType) => void,
    parseHtmlForEditor: (html: string) => string,
    replaceStrongWithBold: (content: string) => string,
    sanitizeContent: (content: string) => string,
}

const EditorialContentPanel: React.FC<Props> = ({ onApproveEditorialContent, onRejectEditorialContent, parseHtmlForEditor, replaceStrongWithBold, sanitizeContent }) => {

    const editorNode = useSelector((app: ApplicationState) => app.comp.editorNode);
    const treeRoot = useSelector((app: ApplicationState) => app.comp.treeRoot);
    const [showRevisions, setShowRevisions] = useState<boolean>(false)
    const mode = useSelector((app: ApplicationState) => app.comp.editorialContentMode);
    const editorStates = useSelector((app: ApplicationState) => app.comp.editorialEditorStates);
    const location = useLocation();
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();
    const selectedEditorialType = useSelector((app: ApplicationState) => app.comp.selectedEditorialType);
    const deleteChecked = useSelector((app: ApplicationState) => app.comp.markForDeleteCheckBox);
    const [hasActiveChange, setHasActiveChange] = useState(false);
    const [contentChanged, setContentChanged] = useState<boolean>(false)
    const [showToolBar, setShowToolBar] = useState<boolean>(true)
    const [readOnly, setReadOnly] = useState<boolean>(false)
    const [annoEditorNode, setAnnoEditorNode] = useState<any | undefined>(EditorialLevelAnnotation)
    const [selectedNodeId, setSelectedNodeId] = useState<number | undefined>(0);
    const [workingCopyEditorState, setWorkingCopyEditorState] = useState<string>("");
    const handleContentChange = (val: boolean) => setContentChanged(val);
    const currentEditorTextForAnnotation = useSelector((app: ApplicationState) => app.comp.currentEditorTextForAnnotation);

    //Prevents unnecessary re-calculations when editorNode.nodeType hasn't changed
    const allowedEditorialTypes = useMemo(() =>
        editorNode === undefined ? [] : findAllowedEditorialTypesForNodeType(editorNode.nodeType),
        [editorNode]
    );

    const onShowRevisionsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowRevisions(event.target.checked);
    };

    const handleDelete = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ComponentManagementActionType.MARK_FOR_DELETION, mark: event.target.checked });
        markForDeletion()
    };

    const markForDeletion = () => {
        if (editorNode && selectedEditorialType) {
            saveEditorialContent(editorNode, selectedEditorialType, { content: "", markedForDeletion: true })
                .then(response => {
                    dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
                });
        }
    }

    useEffect(() => {
        let editorialTypeParameter = qs.parse(location.search, { ignoreQueryPrefix: true }).editorial_type;
        let editorialType = editorialTypeParameter ? (editorialTypeParameter as EditorialType) : undefined;
        if (editorialType === undefined && allowedEditorialTypes.length > 0) {
            editorialType = allowedEditorialTypes[0];
        }
        dispatch({ type: ComponentManagementActionType.SELECTED_EDITORIAL_TYPE_CHANGED, selectedEditorialType: editorialType });
    }, [location.search, allowedEditorialTypes, dispatch]);

    useEffect(() => {
        dispatch({ type: ComponentManagementActionType.MARK_FOR_DELETION, mark: false });
        if (!editorNode) {
            return;
        }
        let newEditorStates = new Map<EditorialType, string>();

        // we recieve an array of arrays
        for (const ec of editorNode.editorialContent) {
            if (selectedEditorialType && ec.editorialType === selectedEditorialType) {
                if (ec.markedForDeletion === true) {
                    dispatch({ type: ComponentManagementActionType.MARK_FOR_DELETION, mark: true });
                }
            }
            newEditorStates.set(ec.editorialType, ec.workingCopy);
        }

        if (selectedEditorialType === EditorialType.ANNOTATION) {
            if (editorNode.annotations?.markedForDeletion) {
                dispatch({ type: ComponentManagementActionType.MARK_FOR_DELETION, mark: true });
            }
        }

        for (const type of allowedEditorialTypes) {
            if (!newEditorStates.has(type)) {
                newEditorStates.set(type, "");
            }
        }

        dispatch({ type: ComponentManagementActionType.EDITORIAL_STATES_CHANGED, editorialStates: newEditorStates });
    }, [editorNode, allowedEditorialTypes, selectedEditorialType, dispatch]);

    useEffect(() => {
        if (history.location.pathname && selectedEditorialType && mode === "edit") {
            dispatch({ type: ComponentManagementActionType.EDITORIAL_CONTENT_MODE_CHANGED, editorialContentMode: "view" });
        }
    }, [history.location.pathname, selectedEditorialType, dispatch])

    useEffect(() => {
        setShowToolBar(true);
        setReadOnly(false);
        if (selectedEditorialType === EditorialType.ANNOTATION) {
            setShowToolBar(false);
            setReadOnly(true);
            setSelectedNodeId(0);
            setAnnoEditorNode(EditorialLevelAnnotation);
            setWorkingCopyEditorState("");
        }
    }, [selectedEditorialType, mode])

    useEffect(() => {
        const cleanText = (text: string | undefined) =>
            text?.replace(/\u200B/g, '').replace(/^<p>([\s\S]*?)<\/p>$/, '$1');
        const currentText = cleanText(currentEditorTextForAnnotation);
        let nodeText = cleanText(annoEditorNode?.workingCopy);
        dispatch({
            type: ComponentManagementActionType.SELECTED_ANNO_TYPE_CHANGED,
            selectedAnnoType: annoEditorNode?.type
        });

        if (mode === "edit" && currentText !== undefined && nodeText !== undefined && currentText.localeCompare(nodeText) !== 0) {
            dispatch({
                type: ComponentManagementActionType.ANNO_HAS_CHANGES,
                annoChanges: true,
            });
        } else {
            dispatch({
                type: ComponentManagementActionType.ANNO_HAS_CHANGES,
                annoChanges: false,
            });
        }
    }, [annoEditorNode, currentEditorTextForAnnotation, dispatch, mode]);

    if (!selectedEditorialType || !editorNode) {
        return <></>
    }
    let edTypeNode = editorNode.editorialContent.find((ec) => ec.editorialType === selectedEditorialType)
    let testNode = selectedEditorialType === EditorialType.ANNOTATION ? editorNode?.annotations?.workingCopy || '' : editorStates.get(selectedEditorialType)!

    function getHtml(defaultState: string, selectedEditorialType: EditorialType) {
        if (!defaultState) {
            let placeholder = getEditorialPlaceholder(selectedEditorialType);
            if (placeholder !== undefined) {
                return placeholder;
            }
            return defaultState;
        }
        return defaultState;
    }

    const handleActiveChange = (isChange: boolean) => {
        setHasActiveChange(isChange)
    }

    const initialContent = parseHtmlForEditor(getHtml(editorStates.get(selectedEditorialType)!, selectedEditorialType));

    const handleNodeChange = (subTree: AnnoNode | Annotation) => {
        setShowToolBar(false);
        setReadOnly(false);
        if ('annoId' in subTree) {
            setAnnoEditorNode(subTree)
            setSelectedNodeId(subTree.annoId)
        }
        if (!('type' in subTree)) {
            setShowToolBar(false);
            setReadOnly(true);
            setAnnoEditorNode(EditorialLevelAnnotation)
            setSelectedNodeId(0)
        }
        if ('type' in subTree && subTree.type === 'ANNOTEXT') {
            setShowToolBar(true);
            setReadOnly(false);
        }
    }

    const tabPlainText = (editorialType: EditorialType) => {
        if (editorialType === EditorialType.ANNOTATION) {
            return editorNode?.annotations?.workingCopy ?? '';
        }
        return editorStates.get(editorialType) ?? "";
    };

    const getContentForTab = (editorialContentType: EditorialType) => {
        if (editorialContentType === EditorialType.ANNOTATION) {
            return editorNode.annotations;
        }
        return getEditorialContentByType(editorNode.editorialContent, editorialContentType);
    }

    const getTabLabel = (editorialType: EditorialType, editorialContent: any) => {
        if (editorialType === EditorialType.ANNOTATION) {
            return (editorialContent?.subTreeHasUnapprovedChanges ? "*" : "") + getEditorialContentTypeName(editorialType as EditorialType);
        }
        return (editorialContent?.status === EditorialStatus.NOT_APPROVED ? "*" : "") + getEditorialContentTypeName(editorialType as EditorialType);
    }

    const getHTMLForEditorial = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION) {
            return parseHtmlForEditor(annoEditorNode?.workingCopy);
        }
        return parseHtmlForEditor(getHtml(editorStates.get(selectedEditorialType)!, selectedEditorialType))
    }
    const getApprovedCopyForRevisions = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION && editorNode.annotations) {
            const { approvedCopy } = editorNode.annotations;
            return approvedCopy;
        }
        return edTypeNode?.approvedCopy!;
    }

    const getWorkingCopyForRevisions = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION && editorNode.annotations) {
            const { workingCopy } = editorNode.annotations;
            return workingCopy;
        }
        return editorStates.get(selectedEditorialType);
    }

    const isShowRevisionsForEC = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION && editorNode.annotations) {
            const { approvedCopy, workingCopy } = editorNode.annotations;
            return isShowRevisions(approvedCopy, workingCopy);
        }
        return isShowRevisions(edTypeNode?.approvedCopy!, edTypeNode?.workingCopy!);
    }

    const getWorkingCopyForDMP = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION) {
            return workingCopyEditorState || annoEditorNode?.workingCopy!;
        }
        return editorStates.get(selectedEditorialType);
    }

    const getApprovedCopyForDMP = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION) {
            return annoEditorNode?.approvedCopy!;
        }
        return edTypeNode?.approvedCopy!;
    }

    const displayApprovedDate = () => {
        if (selectedEditorialType !== EditorialType.ANNOTATION &&
            edTypeNode?.lastApprovedDate) {
            let approvedDate = new Date(edTypeNode?.lastApprovedDate);
            return "Last Approved on " + approvedDate.toLocaleDateString();
        }
        return "";
    }

    const isMarkForDeletionDisabled = !getApprovedCopyForRevisions();

    return (
        <>
            <RouteLeavingGuard
                checkFullLocation={true}
                when={mode === "edit"}
                text={!hasActiveChange ? "Are you sure you want to exit edit mode?" : "You have made a change. Are you sure you want to exit? All changes will be lost"}
                navigate={(path: string) => history.push(path)}
                shouldBlockNavigation={(location: string) => {
                    return true;
                }}
            />
            <ApproveEditorialContentDialog onApproveEditorialContent={onApproveEditorialContent} />
            <RejectEditorialContentDialog onRejectEditorialContent={onRejectEditorialContent} />
            <Grid container direction='column'>
                <Grid item>
                    <AntTabs value={allowedEditorialTypes.findIndex(e => e === selectedEditorialType)} onChange={(_, value) => {
                        let editorialContentType = allowedEditorialTypes[value];
                        if (editorialContentType !== selectedEditorialType) {
                            let subTree = findSubTreeByNodeId(treeRoot!, editorNode.nodeId);
                            if (subTree) {
                                buildComponentPageURL(treeRoot!, subTree, editorialContentType, history);
                            }
                        }
                    }}>
                        {allowedEditorialTypes.map((editorialType, _) => {
                            let editorialContentType = editorialType;
                            let editorialContent = getContentForTab(editorialContentType);
                            let tabClassName = classes.emptyTab;
                            let plainText: string = tabPlainText(editorialType);
                            if (plainText.trim().length > 0) { // has some content
                                tabClassName = classes.tabWithContent;
                            }
                            if (editorialContent?.status === EditorialStatus.NOT_APPROVED) {
                                tabClassName = classes.tabWithNotApprovedChanges;
                            }
                            if (editorialType === EditorialType.ANNOTATION && editorNode.annotations?.subTreeHasUnapprovedChanges) {
                                tabClassName = classes.tabWithNotApprovedChanges;
                            }
                            return (
                                <AntTab
                                    className={tabClassName}
                                    key={editorialType}
                                    label={getTabLabel(editorialType, editorialContent)}
                                />
                            )
                        })}
                    </AntTabs>
                </Grid>
                <Grid item className={classes.editorArea}>
                    {editorStates.get(selectedEditorialType) !== undefined &&
                        <Grid container direction='column' className="flex-gap-1" wrap='nowrap'>
                            {mode === "edit" ? (
                                <TextEditorDialog
                                    title={`Editing ${getEditorialContentTypeName(selectedEditorialType)}`}
                                    citation={editorNode.citation}
                                    open={true}
                                    annoNodes={editorNode?.annotations}
                                    showTreeView={selectedEditorialType === EditorialType.ANNOTATION}
                                    onNodeClicked={(subTree: AnnoNode) => handleNodeChange(subTree)}
                                    selectedNodeId={selectedEditorialType === EditorialType.ANNOTATION ? selectedNodeId : undefined}
                                    Actions={
                                        <EditorialContentToolbar
                                            hasActiveChange={hasActiveChange}
                                            handleActiveChange={handleActiveChange}
                                            contentChanged={contentChanged}
                                            setContentChanged={setContentChanged}
                                            editingNodeId={annoEditorNode?.annoId}
                                        />
                                    }
                                >
                                    <Grid container direction='column' className='editor-dialog'>
                                        <Grid item>
                                            <TextEditor
                                                showToolbar={showToolBar}
                                                readOnly={readOnly}
                                                showEditorialTemplates={true}
                                                editorStyle={{}}
                                                html={getHTMLForEditorial()}
                                                selectedEditorialType={selectedEditorialType}
                                                onChange={(newContent: string) => {
                                                    newContent = newContent.replace(/\u200B/g, '')
                                                    const cleanedContent = newContent.trim();
                                                    const content = (['<p><br></p>', '<p><br/></p>'].includes(cleanedContent) && selectedEditorialType !== EditorialType.ANNOTATION)
                                                        ? ''
                                                        : newContent;
                                                    const parsedContent = replaceStrongWithBold(content);
                                                    const parsedContentWithEntities = sanitizeContent(parsedContent);
                                                    handleActiveChange(true);
                                                    handleContentChange(!isEqual(initialContent, newContent));
                                                    dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                                    setWorkingCopyEditorState(parsedContentWithEntities);
                                                    let newStates = new Map<EditorialType, string>(editorStates.set(selectedEditorialType, parsedContentWithEntities));
                                                    dispatch({
                                                        type: ComponentManagementActionType.EDITORIAL_STATES_CHANGED,
                                                        editorialStates: newStates,
                                                        currentEditorTextForAnnotation: parsedContentWithEntities.replace(/<br>/g, "<br/>")
                                                    });
                                                }}
                                            />
                                        </Grid>
                                        <Grid item className={classes.diffCSS} style={{ height: '50%' }}>
                                            <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px', fontSize: "24px" }}>REVISIONS:</h1>
                                            <DMP string1={getApprovedCopyForDMP()} string2={getWorkingCopyForDMP()} />
                                        </Grid>
                                    </Grid>
                                </TextEditorDialog>
                            ) : (
                                <>
                                    <FormGroup style={{ flexDirection: 'row', paddingLeft: 4 }}>
                                        <FormControlLabel control={<Checkbox
                                            checked={showRevisions}
                                            onChange={onShowRevisionsChanged}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />} label="Show Revisions" />
                                        <FormControlLabel control={<Checkbox
                                            checked={deleteChecked}
                                            onChange={handleDelete}
                                            disabled={isMarkForDeletionDisabled}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />} label="Mark For Deletion" />
                                    </FormGroup>
                                    {editorNode &&
                                        <Grid item style={{ paddingLeft: '8px' }}>
                                            <Typography variant='h5' className={classes.approvedDate}>{displayApprovedDate()}</Typography>
                                        </Grid>
                                    }
                                    <Grid item className={classes.buttonArea}>
                                        <EditorialContentToolbar
                                            hasActiveChange={hasActiveChange}
                                            handleActiveChange={handleActiveChange}
                                            contentChanged={contentChanged}
                                            setContentChanged={setContentChanged}
                                        />
                                    </Grid>
                                    <TextEditor
                                        showToolbar={false}
                                        readOnly={true}
                                        editorStyle={{ maxHeight: 200 }}
                                        selectedEditorialType={selectedEditorialType}
                                        html={testNode && parseHtmlForEditor(getHtml(testNode, selectedEditorialType))}
                                        onChange={(newValue: string) => {
                                            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                        }}
                                    />
                                    {isShowRevisionsForEC() && showRevisions && !editorNode?.repealed &&
                                        <div className={classes.diffCSS}>
                                            <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px', fontSize: "24px" }}>REVISIONS:</h1>
                                            <DMP string1={getApprovedCopyForRevisions()} string2={getWorkingCopyForRevisions()} />
                                        </div>
                                    }
                                    {!isShowRevisionsForEC() && showRevisions && !editorNode?.repealed &&
                                        <div>
                                            <p style={{ fontWeight: 'bold', paddingBottom: '10px', fontSize: "24px", color: '#c44040' }}>No Revisions</p>
                                        </div>
                                    }
                                    {editorNode.repealed && showRevisions &&
                                        <div>
                                            <p style={{ fontWeight: 'bold', paddingBottom: '10px', fontSize: "28px", color: '#c44040' }}>Provision Marked for Repeal.</p>
                                        </div>
                                    }
                                </>
                            )}
                            <Grid item>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
        </>
    )
}

export default EditorialContentPanel;
